import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  SmallDashOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  Alert,
  Button,
  Checkbox, 
  Dropdown,
  Input,
  Menu,
  Space,
  Table,
  Tooltip,
  message,
} from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Stack from "@mui/material/Stack";
import { Chip, Badge } from "@mui/material";
import EditNotificationsOutlinedIcon from "@mui/icons-material/EditNotificationsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format } from "date-fns";

import firebase from "../../../config/firebase";
import dayjs from "dayjs";
import DatePickerMenu from "../../../components/dataPicker/dataPickerMenu";
import DialogFileDriver from "../../../components/dialog-fila-driver/dialog-fila-driver";
import DialogFilaTransport, {
  parseTransportQueueResponse,
} from "../../../components/dialog-fila-transport/dialog-fila-transport";
import {
  acceptTransportFromQueue,
  getAcceptTransportErrorMessage,
} from "../../../components/notifications/acceptTransportFromQueue";
import ModalFilter from "../../../components/ModalFilter/ModalFilter";
import {
  parseStoredColumnOrder,
  useDraggableColumnOrder,
} from "../../../components/column-order-manager";
import YesOrNot from "./yesOrNot";
import freightApi2 from "../../../service/modules/freight.api";
import paymentService from "../../../service/payment.service";
import {
  mapFreightFromApi,
  parseFreightV2ListResponse,
  buildFreightV2ScopeParams,
} from "../../../util/freight/freight-list-mapper";
import { shouldHideDriverNegotiationOnFreightRecord } from "../../../util/freight/freightOwnershipVisibility";
import {
  peekShipperDocCache,
  setShipperDocCache,
} from "../../../util/shipper/shipperDocCache";
import {
  FREIGHT_STATUS_DESCRIBE_OPTIONS,
  STOPPING_POINT_ACTIVE_STATUS_CODES,
  translatorForTheStatusOfStoppingPoints,
  normalizeFreightStatusDescribe,
  mapFreightStatusFilterForV2Api,
} from "../../frete-copy/action/action";

const menuItemStyle = {
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 14px",
  color: "#012442",
  fontWeight: 500,
};

const TTL_STOPPING_POINTS_MS = 10 * 60 * 1000;
const TTL_QUEUE_COUNT_MS = 60 * 1000;

const ENRICH_CONCURRENCY = 12;
const ENRICH_BATCH_MERGE = 20;
const REALTIME_REFRESH_DEBOUNCE_MS = 1500;
/** Debounce curto só para patch de status operacional (SP / activeStatus). */
const REALTIME_STATUS_REFRESH_DEBOUNCE_MS = 400;
/** Limite do operador `in` no Firestore (Web SDK v9). */
const FIRESTORE_IN_CHUNK_SIZE = 30;

/**
 * Timing só para análise de performance da listagem.
 * Não altera fluxo. Ativar:
 *   localStorage.setItem('fortio:freightListPerf', '1')
 * ou URL ?freightListPerf=1
 * Desligar: localStorage.setItem('fortio:freightListPerf', '0')
 * Em development fica ligado por padrão (salvo se forçado a '0').
 */
function isFreightListPerfEnabled() {
  try {
    if (typeof window === "undefined") return false;
    const forced = window.localStorage.getItem("fortio:freightListPerf");
    if (forced === "0") return false;
    if (forced === "1") return true;
    if (
      new URLSearchParams(window.location.search).get("freightListPerf") === "1"
    ) {
      return true;
    }
    return process.env.NODE_ENV === "development";
  } catch {
    return false;
  }
}

let freightListPerfSeq = 0;

function freightListPerf(label, detail) {
  if (!isFreightListPerfEnabled()) return;
  const ts = performance.now().toFixed(1);
  if (detail !== undefined) {
    // eslint-disable-next-line no-console
    console.log(`[FreteListPerf +${ts}ms] ${label}`, detail);
  } else {
    // eslint-disable-next-line no-console
    console.log(`[FreteListPerf +${ts}ms] ${label}`);
  }
}

function freightListPerfStart(name) {
  if (!isFreightListPerfEnabled()) return null;
  const mark = `${name}-${++freightListPerfSeq}`;
  try {
    performance.mark(`${mark}-start`);
  } catch {
    // noop
  }
  return { mark, t0: performance.now() };
}

function freightListPerfEnd(handle, label, detail) {
  if (!handle || !isFreightListPerfEnabled()) return;
  const ms = performance.now() - handle.t0;
  try {
    performance.mark(`${handle.mark}-end`);
    performance.measure(handle.mark, `${handle.mark}-start`, `${handle.mark}-end`);
  } catch {
    // noop
  }
  freightListPerf(`${label} — ${ms.toFixed(0)}ms`, detail);
}

function chunkArray(arr, size) {
  if (!Array.isArray(arr) || size <= 0) return [];
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

function cacheGet(map, key, now = Date.now()) {
  const e = map.get(key);
  if (!e) return undefined;
  if (e.expiresAt <= now) {
    map.delete(key);
    return undefined;
  }
  return e.value;
}

function cacheSet(map, key, value, ttlMs, now = Date.now()) {
  map.set(key, { value, expiresAt: now + ttlMs });
}

function pruneExpiredEntries(map, now = Date.now()) {
  for (const [key, entry] of map.entries()) {
    if (!entry || entry.expiresAt <= now) map.delete(key);
  }
}

function deleteTransportQueueCacheForFreight(map, freightId) {
  if (!freightId) return;
  const prefix = `${freightId}:`;
  for (const key of map.keys()) {
    if (key === freightId || key.startsWith(prefix)) {
      map.delete(key);
    }
  }
}

function resolveEmbarcadorUid(record, user) {
  const fromRecord =
    record?.clientPayment?.uid ||
    record?.shipper?.uid ||
    record?.idShipper ||
    "";
  return String(fromRecord || user?.uidShipper || "").trim();
}

async function mapPool(items, limit, iterator) {
  if (!items.length) return [];
  const results = new Array(items.length);
  let nextIndex = 0;
  async function worker() {
    while (true) {
      const i = nextIndex++;
      if (i >= items.length) break;
      results[i] = await iterator(items[i], i);
    }
  }
  const n = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: n }, () => worker()));
  return results;
}

function fingerprintFreightParams(params) {
  return [
    params.uidShipper ?? "",
    params.idUser ?? "",
    params.affiliates ?? "",
    String(params.viewAll ?? ""),
    String(params.isTransportadora ?? ""),
    params.filterKey ?? "",
  ].join("|");
}

function shipperIsTransport(shipper) {
  if (!shipper || typeof shipper !== "object") return false;
  const value = shipper.isTransport;
  return (
    value === true ||
    value === 1 ||
    String(value).toLowerCase() === "true"
  );
}

function isUserTransportadora(user, shipper) {
  return (
    shipperIsTransport(shipper) ||
    user?.Transportadora === true ||
    user?.transportadora === true
  );
}

function applyFreightV2ScopeParams(params, user, shipper, options = {}) {
  Object.assign(params, buildFreightV2ScopeParams(user, shipper, options));
}

/** Describe do frete em trânsito (código 05) — alinhado a `STATUS_DESCRIBE_BY_CODE` */
const FREIGHT_IN_TRANSIT_DESCRIBE = "Em transito";
const FREIGHT_IN_TRANSIT_MACRO_CODE = "EM_TRANSITO_FRETE";
const STOPPING_IN_TRANSIT_CODE = "IN_TRANSIT";

/** Remove acentos para busca/comparação (NFD + retirar marcas combinantes). */
function foldDiacritics(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase();
}

/** Parada `IN_TRANSIT` não entra na lista: unificada com o describe do frete em trânsito */
const STOPPING_POINT_CODES_OMITTED_FROM_FILTER = new Set([
  STOPPING_IN_TRANSIT_CODE,
]);

/** Frete (describe 00–08) + paradas (enum) */
const STATUS_FILTER_OPTION_VALUES = [
  ...FREIGHT_STATUS_DESCRIBE_OPTIONS,
  ...STOPPING_POINT_ACTIVE_STATUS_CODES.filter(
    (c) => !STOPPING_POINT_CODES_OMITTED_FROM_FILTER.has(c),
  ),
];

/** Rótulo no filtro (PT); paradas: código do enum; frete: texto describe */
function stoppingPointStatusFilterLabel(code) {
  const t = translatorForTheStatusOfStoppingPoints(code);
  const base =
    typeof t === "string" && t.trim() ? t.trim() : String(code ?? "");
  if (code === "LOADING_PENDING") return `${base} (coleta)`;
  if (code === "UNLOADING_PENDING") return `${base} (entrega)`;
  return base;
}

/** Ordenação local quando existe `activeStatus` (mesma ordem do enum operacional) */
const ACTIVE_STATUS_SORT_RANK = {
  LOADING_PENDING: 1,
  HEADING_TO_PICKUP: 2,
  WAITING_LOADING: 3,
  WAITING_DOCUMENTATION: 4,
  LOADING_COMPLETED: 5,
  [STOPPING_IN_TRANSIT_CODE]: 6,
  UNLOADING_PENDING: 7,
  WAITING_UNLOADING: 8,
  UNLOADING_COMPLETED: 9,
};

function statusFilterOptionLabel(value) {
  if (STOPPING_POINT_ACTIVE_STATUS_CODES.includes(value)) {
    return stoppingPointStatusFilterLabel(value);
  }
  if (value === "Pendente de validação") return "P. Validação";
  if (value === "Pendente de contratação") return "P.Contratação";
  if (value === FREIGHT_IN_TRANSIT_DESCRIBE) return "Em trânsito";
  return value;
}

function filterStatusFilterOptions(searchText) {
  const q = String(searchText ?? "").trim().toLowerCase();
  const qFold = foldDiacritics(q);
  if (!q) return [...STATUS_FILTER_OPTION_VALUES];
  return STATUS_FILTER_OPTION_VALUES.filter((value) => {
    const label = String(statusFilterOptionLabel(value)).toLowerCase();
    const raw = String(value).toLowerCase();
    return (
      label.includes(q) ||
      raw.includes(q) ||
      foldDiacritics(label).includes(qFold) ||
      foldDiacritics(raw).includes(qFold)
    );
  });
}

function serializeFilterKey(dateRange, columnFilters) {
  try {
    return JSON.stringify({
      dr: Array.isArray(dateRange)
        ? dateRange.map((d) =>
            d == null ? null : dayjs(d).format("YYYY-MM-DD"),
          )
        : [],
      cf: columnFilters,
    });
  } catch {
    return "";
  }
}

function normalizeColumnFiltersForV2(columnFilters) {
  if (!columnFilters || typeof columnFilters !== "object") return {};
  const out = { ...columnFilters };
  const st = out["status.describe"];
  if (Array.isArray(st)) {
    const mapped = st
      .map((value) => {
        const s = String(value ?? "").trim();
        if (s === STOPPING_IN_TRANSIT_CODE) return FREIGHT_IN_TRANSIT_DESCRIBE;
        return normalizeFreightStatusDescribe(s) || s;
      })
      .filter(Boolean);
    out["status.describe"] = [...new Set(mapped)];
  } else if (st != null && st !== "") {
    const s = String(st).trim();
    const norm =
      s === STOPPING_IN_TRANSIT_CODE
        ? FREIGHT_IN_TRANSIT_DESCRIBE
        : normalizeFreightStatusDescribe(s) || s;
    out["status.describe"] = [norm];
  }
  return out;
}

/**
 * Parâmetros de filtro do GET /freight/v2 (query string).
 * Valores monetários: número exato como string (paridade com matchesV2Filters no back).
 */
function buildV2FilterQueryParams(dateRange, columnFilters) {
  const cf = normalizeColumnFiltersForV2(columnFilters);
  const q = {};

  if (dateRange && dateRange.length) {
    const [a, b] = dateRange;
    if (a) q.dateColetaDe = dayjs(a).format("YYYY-MM-DD");
    if (b) q.dateColetaAte = dayjs(b).format("YYYY-MM-DD");
  }

  if (cf["numberSerial"])
    q.numberSerial = String(cf["numberSerial"]).trim();
  if (cf["clientPayment.name"])
    q.clientPaymentName = String(cf["clientPayment.name"]).trim();
  if (cf["freight.getDriverFreight.name"])
    q.driverName = String(cf["freight.getDriverFreight.name"]).trim();
  if (cf["firstDelivery.city"])
    q.origem = String(cf["firstDelivery.city"]).trim();
  if (cf["lastDelivery.city"])
    q.destino = String(cf["lastDelivery.city"]).trim();

  appendMoneyFilterToV2Query(q, cf["freight.valueFreightage"], "freightValue");
  appendMoneyFilterToV2Query(q, cf["driver.valueNegotiated"], "payingDriver");

  const st = cf["status.describe"];
  if (Array.isArray(st) && st.length) {
    const freightSet = new Set(FREIGHT_STATUS_DESCRIBE_OPTIONS);
    const values = st
      .map((c) => String(c ?? "").trim())
      .filter(
        (c) =>
          STOPPING_POINT_ACTIVE_STATUS_CODES.includes(c) || freightSet.has(c),
      );
    const normalized = [...new Set(values)].map((v) => {
      if (v === FREIGHT_IN_TRANSIT_DESCRIBE) return FREIGHT_IN_TRANSIT_MACRO_CODE;
      return mapFreightStatusFilterForV2Api(v);
    });
    if (normalized.length === 1) {
      // Backend v2 works better with scalar status value.
      q.status = normalized[0];
    } else if (normalized.length > 1) {
      q.status = normalized;
    }
  }

  return q;
}

function omitRecentlyDeletedRows(rows, deletedIds) {
  if (!Array.isArray(rows) || !deletedIds?.size) return rows;
  return rows.filter((row) => row?.id && !deletedIds.has(row.id));
}

/** Mapeia o retorno do backend — ver util/freight/freight-list-mapper.js */

function moneyToInt(value) {
  if (value == null) return 0;
  if (typeof value === "number") return Math.round(value);
  const str = String(value);
  return Math.round(Number(str.replace(/\./g, "").replace(",", ".")));
}

function appendMoneyFilterToV2Query(q, fieldValue, queryKey) {
  if (fieldValue == null || fieldValue === "") return;
  if (typeof fieldValue === "object" && fieldValue.operator) {
    if (fieldValue.operator === "equals" && fieldValue.value !== undefined)
      q[queryKey] = String(Number(fieldValue.value));
  } else {
    const n = typeof fieldValue === "number" ? fieldValue : moneyToInt(fieldValue);
    q[queryKey] = String(Number(n));
  }
}

/** Há filtros enviados ao GET /freight/v2 (coluna ou data). */
function hasActiveV2Filters(dateRange, columnFilters) {
  const q = buildV2FilterQueryParams(dateRange, columnFilters);
  return Object.keys(q).length > 0;
}

function renderStatusColor(status) {
  const chipStyle = { minWidth: "100px", maxWidth: "150px" };
  const commonChip = (label, color) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Chip
        label={label}
        sx={{
          ...chipStyle,
          backgroundColor: color,
          color: "#F9FAFB",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          height: 28,
          px: 0.5,
          boxShadow:
            "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
          border: "1px solid rgba(1,36,66,0.08)",
          "& .MuiChip-label": {
            px: 1,
          },
        }}
      />
    </Stack>
  );

  switch (status) {
    
    case "Contratado":
      return commonChip(status, "#059669");
    case "P. Validação":
    case "Pendente de validação":
      return commonChip("P. Validação", "#A855F7");
    case "P.Contratação":
    case "Pendente de contratação":
      return commonChip("P.Contratação", "#F97316");
    case FREIGHT_IN_TRANSIT_DESCRIBE:
    case "Em trânsito":
      return commonChip("Em trânsito", "#16A34A");
    case "Entregue":
      return commonChip(status, "#2563EB");
    case "Finalizado":
      return commonChip(status, "#6B7280");
    case "Cancelado":
      return commonChip(status, "#B91C1C");
    case "Deslocando-se para a coleta":
      return commonChip(status, "#22C55E");
    case "Aguardando Carregamento":
      return commonChip(status, "#F59E0B");
    case "Aguardando Documentação":
      return commonChip(status, "#FBBF24");
    case "Aguardando Descarregamento":
      return commonChip(status, "#EAB308");
    case "Criado, não iniciado":
      return commonChip(status, "#64748B");
    case "Etapa de carregamento finalizada":
      return commonChip(status, "#0D9488");
    case "Etapa de descarregamento finalizada":
      return commonChip(status, "#0284C7");
    default:
      return commonChip(status || "—", "#020202");
  }
}

/** Status operacionais terminais / ainda não iniciados — não representam a etapa atual. */
const INACTIVE_LOADING_STATUSES = new Set([
  "LOADING_PENDING",
  "LOADING_COMPLETED",
]);
const INACTIVE_UNLOADING_STATUSES = new Set([
  "UNLOADING_PENDING",
  "UNLOADING_COMPLETED",
]);

/** Códigos macro do frete que devem prevalecer sobre substatus de parada. */
const FREIGHT_TERMINAL_STATUS_CODES = new Set(["06", "07", "08"]);

function normalizeStoppingPointStatusCode(status) {
  if (typeof status !== "string") return "";
  return status.trim().toUpperCase();
}

/**
 * Substatus da parada em andamento (só a partir de stopping_points).
 * Não usa activeStatus — evita “prender” chip em substatus stale quando
 * todas as paradas já estão inativas (ex.: caminho para Entregue).
 */
function resolveActiveStoppingPointStatusCodeFromStops(stops) {
  if (!Array.isArray(stops) || !stops.length) return null;

  const ordered = [...stops].sort(
    (a, b) => (Number(a?.stop_order) || 0) - (Number(b?.stop_order) || 0),
  );

  for (const point of ordered) {
    if (!point || typeof point !== "object") continue;

    const loadingStatus = normalizeStoppingPointStatusCode(point.loadingStatus);
    const unloadingStatus = normalizeStoppingPointStatusCode(
      point.unloadingStatus,
    );

    const isActiveLoading =
      Boolean(loadingStatus) && !INACTIVE_LOADING_STATUSES.has(loadingStatus);
    const isActiveUnloading =
      Boolean(unloadingStatus) &&
      !INACTIVE_UNLOADING_STATUSES.has(unloadingStatus);

    if (isActiveLoading) return loadingStatus;
    if (isActiveUnloading) return unloadingStatus;
  }

  return null;
}

/**
 * Identifica o substatus do ponto de parada atual (mesma regra do backend
 * `resolveActiveStatusFromStoppingPoints`): percorre as paradas por `stop_order`
 * e retorna o status do primeiro ponto em andamento (nem pendente inicial, nem concluído).
 * Se há paradas mas nenhuma ativa → null (não faz fallback para activeStatus stale).
 */
function resolveCurrentStoppingPointStatusCode(record) {
  const stops = Array.isArray(record?.stopping_points)
    ? record.stopping_points
    : [];

  if (stops.length > 0) {
    return resolveActiveStoppingPointStatusCodeFromStops(stops);
  }

  const apiActive = record?.activeStatus;
  if (apiActive == null || apiActive === "") return null;
  const normalizedApi = normalizeStoppingPointStatusCode(String(apiActive));
  return normalizedApi || null;
}

function translateStoppingPointStatusCode(statusCode) {
  if (!statusCode) return "";
  const translated = translatorForTheStatusOfStoppingPoints(statusCode);
  if (typeof translated === "string" && translated.trim()) {
    return translated.trim();
  }
  return "";
}

function resolveFreightStatusDescribeLabel(record) {
  const raw =
    record?.status?.describe ??
    record?.statusDescribe ??
    record?.status_text ??
    "";
  const normalized =
    normalizeFreightStatusDescribe(String(raw).trim()) || String(raw).trim();
  return normalized;
}

function isFreightTerminalMacroStatus(record) {
  const code = String(record?.status?.code ?? "").trim();
  if (FREIGHT_TERMINAL_STATUS_CODES.has(code)) return true;
  const describe = resolveFreightStatusDescribeLabel(record).toLowerCase();
  return (
    describe === "entregue" ||
    describe === "finalizado" ||
    describe === "cancelado"
  );
}

// ========================
// Badge com dados em memória / sob demanda
// ========================
const DriverBadge = ({
  recordId,
  statusDescribe,
  queueCount = 0,
  queueLoading = false,
}) => {
  if (
    statusDescribe === "Finalizado" ||
    statusDescribe === "Contratado" ||
    statusDescribe === "Cancelado"
  ) {
    return <EditNotificationsOutlinedIcon />;
  }

  return (
    <Link to={"#" + recordId}>
      {queueLoading ? (
        <EditNotificationsOutlinedIcon color="primary" />
      ) : queueCount > 0 ? (
        <Badge badgeContent={queueCount} color="success">
          <EditNotificationsOutlinedIcon color="primary" />
        </Badge>
      ) : (
        <EditNotificationsOutlinedIcon color="primary" />
      )}
    </Link>
  );
};

const TransportBadge = ({
  recordId,
  queueCount = 0,
  queueLoading = false,
}) => (
  <Link to={"#" + recordId}>
    {queueLoading ? (
      <LocalShippingOutlinedIcon color="primary" />
    ) : queueCount > 0 ? (
      <Badge badgeContent={queueCount} color="success">
        <LocalShippingOutlinedIcon color="primary" />
      </Badge>
    ) : (
      <LocalShippingOutlinedIcon color="primary" />
    )}
  </Link>
);


// ========================
// Componente principal
// ========================
const DataFreteTableCopy = ({
  cleanFilter,
  openFilter,
  onCleanFilterHandled,
  onOpenFilterHandled,
  includeAllFreights = false,
}) => {
  const user = useSelector((state) => state.user);
  const shipper = useSelector((state) => state.shipper);
  const [resolvedShipper, setResolvedShipper] = useState(null);
  /** true até resolver escopo de transportadora (evita 1º fetch com params incompletos). */
  const [shipperScopeLoading, setShipperScopeLoading] = useState(() => {
    if (shipperIsTransport(shipper)) return false;
    return Boolean(String(user?.uidShipper ?? "").trim());
  });

  const effectiveShipper = resolvedShipper ?? shipper ?? null;
  const isTransportadoraUser = useMemo(
    () => isUserTransportadora(user, effectiveShipper),
    [user, effectiveShipper],
  );

  const canViewFreightDriver = user?.permissoes?.freightDriver?.isView;
  const canViewFreightFreight = user?.permissoes?.freightFreight?.isView;
  const db = firebase.firestore();

  useEffect(() => {
    if (shipperIsTransport(shipper)) {
      freightListPerf("shipper: already transport in Redux (skip Firestore)");
      if (user?.uidShipper) setShipperDocCache(user.uidShipper, shipper);
      setResolvedShipper(shipper);
      setShipperScopeLoading(false);
      return undefined;
    }
    const carrierUid = String(user?.uidShipper ?? "").trim();
    if (!carrierUid) {
      freightListPerf("shipper: no uidShipper (skip)");
      setResolvedShipper(null);
      setShipperScopeLoading(false);
      return undefined;
    }

    const cached = peekShipperDocCache(carrierUid);
    if (cached) {
      freightListPerf("shipper: cache hit (skip Firestore)");
      setResolvedShipper(cached);
      setShipperScopeLoading(false);
      return undefined;
    }

    try {
      const raw = localStorage.getItem("shipperData");
      if (raw && raw !== "undefined" && raw !== "null") {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setShipperDocCache(carrierUid, parsed);
          freightListPerf("shipper: localStorage hit (skip Firestore)");
          setResolvedShipper(parsed);
          setShipperScopeLoading(false);
          return undefined;
        }
      }
    } catch {
      // noop — segue para fetch
    }

    let cancelled = false;
    setShipperScopeLoading(true);
    const shipperTiming = freightListPerfStart("shipper-get");
    freightListPerf("shipper: Firestore get start", { carrierUid });
    paymentService
      .searchShipper(carrierUid)
      .then((data) => {
        freightListPerfEnd(shipperTiming, "shipper: Firestore get done", {
          exists: Boolean(data),
        });
        if (!cancelled) {
          setResolvedShipper(data || null);
        }
      })
      .catch((error) => {
        freightListPerfEnd(shipperTiming, "shipper: Firestore get FAILED", {
          message: error?.message,
        });
        console.warn("[FreteList] Não foi possível carregar shipper:", error);
        if (!cancelled) {
          setResolvedShipper(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setShipperScopeLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.uidShipper, shipper]);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openTransportModal, setOpenTransportModal] = useState(false);
  const [openModalFilter, setOpenModalFilter] = useState(false);
  const [driversInFila, setDriversInFila] = useState([]);
  const [transportsInFila, setTransportsInFila] = useState([]);
  const [transportFreightId, setTransportFreightId] = useState(null);
  const [transportFreightShipperUid, setTransportFreightShipperUid] =
    useState(null);
  const [acceptingTransportId, setAcceptingTransportId] = useState(null);
  const [datas, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isFilterApplying, setIsFilterApplying] = useState(false);

  const [dateRange, setDateRange] = useState([]);
  const [draftDateRange, setDraftDateRange] = useState([]);
  const hasLoadedRef = useRef(false);

  const [columnFilters, setColumnFilters] = useState({});
  const [statusSearchText, setStatusSearchText] = useState("");
  const [sorterConfig, setSorterConfig] = useState({
    columnKey: null,
    order: null,
  });

  const [columnOrder, setColumnOrder] = useState(null);

  const filterKey = useMemo(
    () => serializeFilterKey(dateRange, columnFilters),
    [dateRange, columnFilters],
  );

  const serverFiltersActive = useMemo(
    () => hasActiveV2Filters(dateRange, columnFilters),
    [dateRange, columnFilters, filterKey],
  );

  const getStatusDescribeForRecord = useCallback((record) => {
    // Entregue / Finalizado / Cancelado: sempre o macro do frete.
    if (isFreightTerminalMacroStatus(record)) {
      return resolveFreightStatusDescribeLabel(record);
    }

    const currentStopStatusCode = resolveCurrentStoppingPointStatusCode(record);
    const stopLabel = translateStoppingPointStatusCode(currentStopStatusCode);
    if (stopLabel) return stopLabel;

    const freightDescribe = resolveFreightStatusDescribeLabel(record);
    if (freightDescribe) return freightDescribe;

    // Sem paradas carregadas: último recurso é activeStatus da API.
    if (
      (!Array.isArray(record?.stopping_points) ||
        record.stopping_points.length === 0) &&
      record?.activeStatus
    ) {
      return (
        translateStoppingPointStatusCode(
          normalizeStoppingPointStatusCode(String(record.activeStatus)),
        ) || String(record.activeStatus)
      );
    }

    return "";
  }, []);

  // Estados de paginação (cursor no servidor — GET /freight/v2)
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [listHasMore, setListHasMore] = useState(false);
  // Lista completa (todas as páginas),
  // para permitir ordenação client-side sobre o conjunto inteiro.
  const [allDatas, setAllDatas] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  /** Map: número da página (>=2) → cursor a enviar na query para obter essa página */
  const cursorRequestByPageRef = useRef(new Map());
  const trimCursorMapFromPage = useCallback((startPage) => {
    if (!Number.isFinite(startPage)) return;
    const nextMap = new Map();
    cursorRequestByPageRef.current.forEach((value, key) => {
      if (key < startPage) nextMap.set(key, value);
    });
    cursorRequestByPageRef.current = nextMap;
  }, []);

  const searchInput = useRef(null);
  const statusFilterRef = useRef(null);
  const lastFetchSigRef = useRef("");
  /** Gate de concorrência da listagem (mesmo page/filtro) — ignora diffs de fingerprint de shipper. */
  const inFlightListGateRef = useRef("");
  const inFlightFetchSigRef = useRef("");
  const filterKeyForFetchRef = useRef("");
  const pageSizeForFetchRef = useRef(50);
  const includeAllForFetchRef = useRef(false);
  const datasRef = useRef([]);
  const fetchDataRef = useRef(null);
  const realtimeRefreshTimerRef = useRef(null);
  const realtimeStatusTimerRef = useRef(null);

  const listVersionRef = useRef(0);

  const recentlyDeletedIdsRef = useRef(new Set());
  useEffect(() => {
    datasRef.current = datas;
  }, [datas]);

  useEffect(() => {
    isFilterApplyingRef.current = isFilterApplying;
  }, [isFilterApplying]);

  const storageKey = (uid, key) => `${uid}:${key}`;
  // Sinal de "voltando de editar/ver/copiar um frete" — usado pra só reaplicar
  // a ordenação salva quando o retorno é dessa navegação específica (mudar de
  // página ou voltar de um frete), e não quando a lista é aberta do zero
  // (primeira visita, F5, ou vindo de outra seção do sistema tipo "Produto").
  const PRESERVE_SORT_FLAG_KEY = "freightPreserveSortOnReturn";
  const markPreserveSortOnReturn = (uid) => {
    if (!uid) return;
    try {
      sessionStorage.setItem(storageKey(uid, PRESERVE_SORT_FLAG_KEY), "1");
    } catch {
      // noop
    }
  };
  const stoppingPointsCacheRef = useRef(new Map());
  const queueCountCacheRef = useRef(new Map());
  const transportQueueCountCacheRef = useRef(new Map());
  const enrichRunIdRef = useRef(0);
  const enrichAbortRef = useRef({ cancelled: false });
  const isFilterApplyingRef = useRef(false);
  const activeStatusRef = useRef(null);
  const [queueUiByFreightId, setQueueUiByFreightId] = useState({});
  const [transportQueueUiByFreightId, setTransportQueueUiByFreightId] = useState({});

  const clearSessionCaches = useCallback(() => {
    stoppingPointsCacheRef.current.clear();
    queueCountCacheRef.current.clear();
    transportQueueCountCacheRef.current.clear();
    setQueueUiByFreightId({});
    setTransportQueueUiByFreightId({});
  }, []);

  const fetchQueueCountCached = useCallback(async (freightId) => {
    const hit = cacheGet(queueCountCacheRef.current, freightId);
    if (hit !== undefined) return hit;
    try {
      const res = await freightApi2.queue(freightId);
      const queue = Array.isArray(res.data) ? res.data : res.data?.items || [];
      const count = queue.length;
      cacheSet(queueCountCacheRef.current, freightId, count, TTL_QUEUE_COUNT_MS);
      return count;
    } catch {
      return 0;
    }
  }, []);

  const prefetchQueueCountForFreight = useCallback(
    async (freightId) => {
      if (!freightId) return;
      setQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { ...(prev[freightId] || {}), loading: true },
      }));
      const count = await fetchQueueCountCached(freightId);
      setQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { count, loading: false },
      }));
    },
    [fetchQueueCountCached],
  );

  const fetchTransportQueueCountCached = useCallback(async (freightId, shipperUid) => {
    const isShipper = shipperUid || user?.uidShipper || "";
    if (!freightId || !isShipper) return 0;

    const cacheKey = `${freightId}:${isShipper}`;
    const hit = cacheGet(transportQueueCountCacheRef.current, cacheKey);
    if (hit !== undefined) return hit;
    try {
      const res = await freightApi2.transportQueue(freightId, isShipper);
      const queue = parseTransportQueueResponse(res);
      const count = queue.length;
      cacheSet(
        transportQueueCountCacheRef.current,
        cacheKey,
        count,
        TTL_QUEUE_COUNT_MS,
      );
      return count;
    } catch {
      return 0;
    }
  }, [user?.uidShipper]);

  const prefetchTransportQueueCountForFreight = useCallback(
    async (freightId, shipperUid) => {
      if (!freightId) return;
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { ...(prev[freightId] || {}), loading: true },
      }));
      const count = await fetchTransportQueueCountCached(freightId, shipperUid);
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [freightId]: { count, loading: false },
      }));
    },
    [fetchTransportQueueCountCached],
  );

  const handleDateRangeChange = (dates) => {
    setDraftDateRange(dates ? [dates[0], dates[1]] : []);
  };

  const enrichFreightRows = useCallback(
    async (rows, runId) => {
      if (!Array.isArray(rows) || !rows.length) {
        return {
          total: 0,
          stoppingPointsErr: 0,
          cacheHits: 0,
          firestoreReads: 0,
          skipped: 0,
        };
      }

      const sanity = {
        total: rows.length,
        stoppingPointsErr: 0,
        cacheHits: 0,
        firestoreReads: 0,
        skipped: 0,
      };
      let mergeBatch = [];
      const enrichTiming = freightListPerfStart("enrich-stopping-points");

      const rowsToFetch = [];
      for (const item of rows) {
        if (!item?.id) continue;

        if (Array.isArray(item.stopping_points) && item.stopping_points.length > 0) {
          cacheSet(
            stoppingPointsCacheRef.current,
            item.id,
            item.stopping_points,
            TTL_STOPPING_POINTS_MS,
          );
          sanity.skipped++;
          continue;
        }

        // activeStatus do /v2 já alimenta o status da listagem (fallback em
        // resolveCurrentStoppingPointStatusCode) — evita N reads no Firestore.
        if (
          item.activeStatus != null &&
          String(item.activeStatus).trim() !== ""
        ) {
          sanity.skipped++;
          continue;
        }

        // Sem activeStatus/SP no payload: usa status.describe do frete na listagem
        // em vez de N+1 em stopping_points (~2–3s na abertura).
        if (String(item?.status?.describe || "").trim()) {
          sanity.skipped++;
          continue;
        }

        const cached = cacheGet(stoppingPointsCacheRef.current, item.id);
        if (cached !== undefined) {
          sanity.cacheHits++;
          if (Array.isArray(cached) && cached.length > 0) {
            mergeBatch.push({
              id: item.id,
              patch: { stopping_points: cached },
            });
          }
          continue;
        }

        rowsToFetch.push(item);
      }

      freightListPerf("enrich: start", {
        rows: rows.length,
        toFetch: rowsToFetch.length,
        skipped: sanity.skipped,
        cacheHits: sanity.cacheHits,
        concurrency: ENRICH_CONCURRENCY,
        runId,
      });

      const flushMerge = (batch) => {
        if (!batch.length) return;
        if (enrichAbortRef.current.cancelled || runId !== enrichRunIdRef.current) {
          return;
        }
        setData((prev) => {
          const base = omitRecentlyDeletedRows(prev, recentlyDeletedIdsRef.current);
          const byId = new Map(base.map((r) => [r.id, { ...r }]));
          batch.forEach(({ id, patch }) => {
            if (recentlyDeletedIdsRef.current.has(id)) return;
            const cur = byId.get(id);
            if (cur) byId.set(id, { ...cur, ...patch });
          });
          return base.map((r) => byId.get(r.id) || r);
        });
      };

      if (mergeBatch.length) {
        flushMerge(mergeBatch);
        mergeBatch = [];
      }

      if (!rowsToFetch.length) {
        freightListPerfEnd(enrichTiming, "enrich: done (no Firestore reads)", sanity);
        return sanity;
      }

      await mapPool(rowsToFetch, ENRICH_CONCURRENCY, async (item) => {
        if (enrichAbortRef.current.cancelled || runId !== enrichRunIdRef.current)
          return;
        if (!item?.id) return;

        let stoppingPoints = cacheGet(stoppingPointsCacheRef.current, item.id);
        if (stoppingPoints === undefined) {
          try {
            sanity.firestoreReads++;
            const snapshot = await db
              .collection("freight")
              .doc(item.id)
              .collection("stopping_points")
              .get();
            stoppingPoints = snapshot.docs.map((spDoc) => ({
              id: spDoc.id,
              ...spDoc.data(),
            }));
            cacheSet(
              stoppingPointsCacheRef.current,
              item.id,
              stoppingPoints,
              TTL_STOPPING_POINTS_MS,
            );
          } catch {
            stoppingPoints = [];
            sanity.stoppingPointsErr++;
          }
        } else {
          sanity.cacheHits++;
        }

        mergeBatch.push({ id: item.id, patch: { stopping_points: stoppingPoints } });
        if (mergeBatch.length >= ENRICH_BATCH_MERGE) {
          const batch = mergeBatch;
          mergeBatch = [];
          flushMerge(batch);
        }
      });

      flushMerge(mergeBatch);
      freightListPerfEnd(enrichTiming, "enrich: done", sanity);
      return sanity;
    },
    [db],
  );

  /**
   * Tempo real: relê stopping_points só dos fretes que mudaram e atualiza
   * activeStatus / chip — sem N+1 na abertura e sem invalidar filtros.
   */
  const refreshOperationalStatusForIds = useCallback(
    async (freightIds, freightDocPatches = {}) => {
      const ids = [...new Set((freightIds || []).filter(Boolean))];
      if (!ids.length) return;

      const timing = freightListPerfStart("realtime-status-refresh");
      freightListPerf("realtime-status: start", { ids: ids.length });

      const patches = [];

      await mapPool(ids, ENRICH_CONCURRENCY, async (freightId) => {
        const fromDoc = freightDocPatches[freightId] || {};
        let stoppingPoints = [];
        try {
          const snapshot = await db
            .collection("freight")
            .doc(freightId)
            .collection("stopping_points")
            .get();
          stoppingPoints = snapshot.docs.map((spDoc) => ({
            id: spDoc.id,
            ...spDoc.data(),
          }));
          cacheSet(
            stoppingPointsCacheRef.current,
            freightId,
            stoppingPoints,
            TTL_STOPPING_POINTS_MS,
          );
        } catch (err) {
          freightListPerf("realtime-status: SP read failed", {
            freightId,
            message: err?.message,
          });
          stoppingPointsCacheRef.current.delete(freightId);
        }

        const activeFromStops =
          resolveActiveStoppingPointStatusCodeFromStops(stoppingPoints);

        const patch = {
          ...fromDoc,
          stopping_points: stoppingPoints,
        };

        if (activeFromStops) {
          patch.activeStatus = activeFromStops;
        } else if (stoppingPoints.length > 0) {
          // Paradas todas inativas/concluídas → limpa substatus stale e busca
          // status.describe do frete (Entregue / Finalizado).
          patch.activeStatus = null;
          if (!patch.status?.code && !patch.status?.describe) {
            try {
              const freightSnap = await db
                .collection("freight")
                .doc(freightId)
                .get();
              const data = freightSnap.exists ? freightSnap.data() : null;
              if (data?.status) patch.status = data.status;
              if (data?.firstDeliveryCompleted != null) {
                patch.firstDeliveryCompleted = data.firstDeliveryCompleted;
              }
            } catch (err) {
              freightListPerf("realtime-status: freight doc read failed", {
                freightId,
                message: err?.message,
              });
            }
          }
        } else if (
          fromDoc.activeStatus != null &&
          String(fromDoc.activeStatus).trim() !== ""
        ) {
          patch.activeStatus = fromDoc.activeStatus;
        }

        patches.push({
          id: freightId,
          patch,
        });
      });

      if (!patches.length) {
        freightListPerfEnd(timing, "realtime-status: done (empty)");
        return;
      }

      setData((prev) => {
        if (!Array.isArray(prev) || !prev.length) return prev;
        const byId = new Map(patches.map((p) => [p.id, p.patch]));
        let changed = false;
        const next = prev.map((row) => {
          const patch = byId.get(row.id);
          if (!patch) return row;
          changed = true;
          return { ...row, ...patch };
        });
        return changed ? next : prev;
      });

      freightListPerfEnd(timing, "realtime-status: done", {
        patched: patches.length,
      });
    },
    [db],
  );

  const refreshOperationalStatusForIdsRef = useRef(refreshOperationalStatusForIds);
  useEffect(() => {
    refreshOperationalStatusForIdsRef.current = refreshOperationalStatusForIds;
  }, [refreshOperationalStatusForIds]);

  // ========================
  // Fetch API — GET /freight/v2 (filtros e paginação no servidor)
  // ========================
  const fetchData = useCallback(async (options = {}) => {
    const forceRefresh = options.forceRefresh === true;
    const isFilterChange = options.isFilterChange === true;
    const effectivePage = options.page ?? current;
    const listVersionAtFetchStart = listVersionRef.current;
    const filtersActive = hasActiveV2Filters(dateRange, columnFilters);
    // Sempre paginação server-side (mesmo com filtros) — evita varrer dezenas de páginas.
    const limit = Math.min(Math.max(1, pageSize), 100);

    if (!user) return;
    const fetchTiming = freightListPerfStart("fetchData");
    freightListPerf("fetchData: start", {
      page: effectivePage,
      pageSize,
      limit,
      filtersActive,
      fetchAllFilteredResults: false,
      isFilterChange,
      forceRefresh,
      filterKey,
      reason: options.reason || (isFilterChange ? "filterChange" : "default"),
    });
    const now = Date.now();
    pruneExpiredEntries(stoppingPointsCacheRef.current, now);
    pruneExpiredEntries(queueCountCacheRef.current, now);
    pruneExpiredEntries(transportQueueCountCacheRef.current, now);
    if (forceRefresh) {
      lastFetchSigRef.current = "";
      inFlightListGateRef.current = "";
      clearSessionCaches();
    }

    const commitListRows = (rows) => {
      if (listVersionAtFetchStart !== listVersionRef.current) return;
      setData(omitRecentlyDeletedRows(rows, recentlyDeletedIdsRef.current));
    };

    const params = {};
    applyFreightV2ScopeParams(params, user, effectiveShipper, {
      includeAllFreights:
        includeAllFreights === true && user?.viewALL === true,
    });

    Object.assign(params, buildV2FilterQueryParams(dateRange, columnFilters));
    params.limit = limit;

    const cursorForPage =
      effectivePage <= 1
        ? undefined
        : cursorRequestByPageRef.current.get(effectivePage);
    if (effectivePage > 1 && cursorForPage === undefined) {
      freightListPerf("fetchData: aborted (no cursor for page)", {
        effectivePage,
      });
      message.warning(
        "Use a paginação em sequência (não é possível pular páginas).",
      );
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (cursorForPage) params.cursor = cursorForPage;

    const fingerprint = fingerprintFreightParams({ ...params, filterKey });
    const requestSig = `${fingerprint}|${effectivePage}|${limit}|${params.cursor ?? ""}`;
    const listGateSig = `${filterKey}|${effectivePage}|${limit}`;
    if (
      !forceRefresh &&
      inFlightFetchSigRef.current &&
      inFlightFetchSigRef.current === requestSig
    ) {
      freightListPerf("fetchData: skipped (same request in-flight)", {
        requestSig,
      });
      return;
    }
    if (
      !forceRefresh &&
      inFlightListGateRef.current &&
      inFlightListGateRef.current === listGateSig
    ) {
      freightListPerf("fetchData: skipped (list gate in-flight)", {
        listGateSig,
        requestSig,
        inFlight: inFlightFetchSigRef.current,
      });
      return;
    }

    if (!forceRefresh && datasRef.current.length > 0) {
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
      setRefreshing(false);
    }

    enrichAbortRef.current.cancelled = true;
    enrichRunIdRef.current += 1;
    const runId = enrichRunIdRef.current;
    enrichAbortRef.current = { cancelled: false };

    try {
      if (isFilterChange) setIsFilterApplying(true);
      inFlightFetchSigRef.current = requestSig;
      inFlightListGateRef.current = listGateSig;
      /**
       * IMPORTANTE:
       * - Não envie uids/viewALL pelo front como "regra de segurança".
       * - O backend deve usar o usuário do token e aplicar a regra.
       *
       * Se você quiser mandar só pra “debug”, mande e o backend IGNORA se não tiver permissão.
       */
      const apiTiming = freightListPerfStart("api-freight-v2");
      const result = await freightApi2.getAllFreightsV2(params);
      const {
        items,
        activeStatus,
        nextCursor,
        hasMore,
      } = parseFreightV2ListResponse(result, limit, cursorForPage);
      freightListPerfEnd(apiTiming, "api: GET /freight/v2", {
        items: items.length,
        hasMore,
        limit,
        page: effectivePage,
        cursor: Boolean(params.cursor),
      });

      activeStatusRef.current = activeStatus;

      setListHasMore(hasMore);
      if (hasMore) {
        cursorRequestByPageRef.current.set(effectivePage + 1, nextCursor);
      } else {
        trimCursorMapFromPage(effectivePage + 1);
      }

      const mapped = items.map(mapFreightFromApi);

      if (effectivePage > 1 && mapped.length === 0) {
        trimCursorMapFromPage(effectivePage);
        setCurrent((prev) => Math.max(1, Math.min(prev - 1, effectivePage - 1)));
        setLoading(false);
        setRefreshing(false);
        message.warning("Nao ha dados para esta pagina. Retornando para a pagina anterior.");
        freightListPerfEnd(fetchTiming, "fetchData: empty page (rollback)");
        return;
      }

      if (
        runId !== enrichRunIdRef.current ||
        listVersionAtFetchStart !== listVersionRef.current
      ) {
        freightListPerfEnd(fetchTiming, "fetchData: stale (aborted after API)");
        return;
      }

      // Fase 1: render base imediata
      commitListRows(mapped);
      setLoading(false);
      setRefreshing(false);
      if (isFilterChange) setIsFilterApplying(false);
      freightListPerf("fetchData: first page painted", {
        rows: mapped.length,
      });

      const sanity = await enrichFreightRows(mapped, runId);
      if (
        sanity.total > 0 &&
        sanity.stoppingPointsErr / sanity.total > 0.1
      ) {
        message.warning(
          `Muitas falhas ao ler stopping_points no Firestore (${sanity.stoppingPointsErr}/${sanity.total}).`,
        );
      }
      freightListPerfEnd(fetchTiming, "fetchData: complete", {
        rows: mapped.length,
        page: effectivePage,
      });
    } catch (err) {
      if (runId !== enrichRunIdRef.current) return;
      const statusCode = err?.response?.status;
      setRefreshing(false);
      setLoading(false);
      setListHasMore(false);
      trimCursorMapFromPage(current + 1);
      freightListPerfEnd(fetchTiming, "fetchData: FAILED", {
        statusCode,
        message: err?.message,
      });
      if (datasRef.current.length > 0) {
        message.warning("Não foi possível atualizar; exibindo dados anteriores.");
      } else {
        if (isFilterChange && statusCode === 500) {
          message.error("Erro interno ao aplicar filtro de motorista. Tente novamente.");
        } else {
          message.error("Erro ao buscar fretes na API");
        }
        setData([]);
      }
      if (isFilterChange) setIsFilterApplying(false);
      inFlightFetchSigRef.current = "";
      inFlightListGateRef.current = "";
    } finally {
      if (isFilterChange) {
        setIsFilterApplying(false);
      }
      if (inFlightFetchSigRef.current === requestSig) {
        inFlightFetchSigRef.current = "";
      }
      if (inFlightListGateRef.current === listGateSig) {
        inFlightListGateRef.current = "";
      }
    }
  }, [
    clearSessionCaches,
    enrichFreightRows,
    user,
    effectiveShipper,
    isTransportadoraUser,
    includeAllFreights,
    current,
    pageSize,
    filterKey,
    dateRange,
    columnFilters,
    trimCursorMapFromPage,
  ]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    clearSessionCaches();
  }, [user?.uid, clearSessionCaches]);

  useEffect(() => {
    recentlyDeletedIdsRef.current.clear();
    listVersionRef.current += 1;
    setData([]);
    setLoading(true);
    setRefreshing(false);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    if (shipperScopeLoading) {
      freightListPerf("mount-trigger: waiting shipperScopeLoading");
      return;
    }
    const fkChanged = filterKeyForFetchRef.current !== filterKey;
    const szChanged = pageSizeForFetchRef.current !== pageSize;
    const scopeAllChanged =
      includeAllForFetchRef.current !==
      (includeAllFreights === true && user?.viewALL === true);
    filterKeyForFetchRef.current = filterKey;
    pageSizeForFetchRef.current = pageSize;
    includeAllForFetchRef.current =
      includeAllFreights === true && user?.viewALL === true;
    if (fkChanged || szChanged || scopeAllChanged) {
      cursorRequestByPageRef.current = new Map();
      setCurrent(1);
    }
    const effectivePage =
      fkChanged || szChanged || scopeAllChanged ? 1 : current;
    const scopeSig = [
      isTransportadoraUser ? "1" : "0",
      user?.viewALL === true ? "1" : "0",
      includeAllFreights === true ? "1" : "0",
      String(user?.uidShipper ?? "").trim(),
    ].join(":");
    const sig = `${filterKey}|${effectivePage}|${pageSize}|${scopeSig}`;
    if (lastFetchSigRef.current === sig) {
      freightListPerf("mount-trigger: skipped (same sig)", { sig });
      return;
    }
    lastFetchSigRef.current = sig;
    freightListPerf("mount-trigger: calling fetchData", {
      sig,
      fkChanged,
      szChanged,
      shipperScopeLoading,
      effectivePage,
      isTransportadoraUser,
      includeAllFreights,
    });
    // Usa ref para não re-disparar quando `fetchData` recria (ex.: shipper resolve).
    fetchDataRef.current?.({
      page: effectivePage,
      isFilterChange: fkChanged || scopeAllChanged,
    });
  }, [
    user?.uid,
    user?.uidShipper,
    user?.viewALL,
    isTransportadoraUser,
    includeAllFreights,
    shipperScopeLoading,
    filterKey,
    current,
    pageSize,
  ]);

  // ========================
  // Busca completa (todas as páginas), só quando existe ordenação ativa —
  // nos dois modos ("Meus fretes" e "Ver todos os fretes"). Sem ordenação,
  // a lista usa a paginação normal por cursor (rápida, uma página por vez).
  // O parâmetro includeAllFreights só muda o ESCOPO da busca (dados só do
  // usuário vs. da empresa toda) — em ambos os casos, quando ativa, busca tudo.
  //QUASE UM SERVE
  // ========================

  const fetchAllPages = useCallback(async () => {
    // Preguiçoso: só busca todas as páginas quando existir ordenação ativa.
    // Sem ordenação, a lista usa a paginação normal por cursor (rápida),
    // sem precisar trazer tudo à toa.
    if (!sorterConfig?.columnKey || !sorterConfig?.order) return;
    if (!user?.uid) return;
    if (shipperScopeLoading) return;

    setLoadingAll(true);
    try {
      const baseParams = {};
      applyFreightV2ScopeParams(baseParams, user, effectiveShipper, {
        includeAllFreights:
          includeAllFreights === true && user?.viewALL === true,
      });
      Object.assign(baseParams, buildV2FilterQueryParams(dateRange, columnFilters));
      baseParams.limit = 50;

      let allRows = [];
      let cursor;
      let hasMoreLocal = true;
      let guard = 0;

      //loop de 200, verifica se tem mais pagina
      while (hasMoreLocal && guard < 200) {
        const result = await freightApi2.getAllFreightsV2({
          ...baseParams,
          cursor,
        });
        const parsed = parseFreightV2ListResponse(result, baseParams.limit, cursor);
        allRows = allRows.concat(parsed.items.map(mapFreightFromApi));
        cursor = parsed.nextCursor;
        hasMoreLocal = Boolean(cursor) && parsed.hasMore;
        guard += 1;
      }
      const uniqueRows = Array.from(
        new Map(allRows.map((row) => [row.id, row])).values(),
      );
      setAllDatas(uniqueRows);
    } catch (error) {
      message.error("Erro ao carregar lista completa de fretes.");
    } finally {
      setLoadingAll(false);
    }
  }, [
    sorterConfig,
    includeAllFreights,
    user,
    effectiveShipper,
    shipperScopeLoading,
    dateRange,
    columnFilters,
  ]);

  useEffect(() => {
    fetchAllPages();
  }, [fetchAllPages]);

  /**
   * Chave estável dos IDs visíveis: enquanto o conjunto de IDs da página
   * não muda (ex.: enriquecimento via `flushMerge` apenas mescla patches),
   * o listener não é recriado.
   */
  const visibleIdsKey = useMemo(() => {
    if (!Array.isArray(datas) || !datas.length) return "";
    return datas
      .map((row) => row?.id)
      .filter(Boolean)
      .slice()
      .sort()
      .join("|");
  }, [datas]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    if (!visibleIdsKey) return undefined;

    const visibleIds = visibleIdsKey.split("|").filter(Boolean);
    if (!visibleIds.length) return undefined;

    const visibleIdSet = new Set(visibleIds);
    const chunks = chunkArray(visibleIds, FIRESTORE_IN_CHUNK_SIZE);
    const documentId = "__name__";
    freightListPerf("listeners: subscribe", {
      visibleIds: visibleIds.length,
      chunks: chunks.length,
    });

    const handleSnapshot = (snapshot) => {
      if (isFilterApplyingRef.current) {
        return;
      }

      const changedIds = new Set();
      const modifiedIds = new Set();
      const freightDocPatches = {};
      let hasRemovals = false;
      // Ignora "added" do 1º snapshot (ack do subscribe) — evita re-fetch na abertura.
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") return;
        const id = change?.doc?.id;
        if (!id || !visibleIdSet.has(id)) return;
        changedIds.add(id);
        if (change.type === "removed") {
          hasRemovals = true;
        } else if (change.type === "modified") {
          modifiedIds.add(id);
          const data = change.doc.data() || {};
          const patch = {};
          if (data.activeStatus != null && data.activeStatus !== "") {
            patch.activeStatus = data.activeStatus;
          }
          if (data.status && typeof data.status === "object") {
            patch.status = data.status;
          }
          if (data.firstDeliveryCompleted != null) {
            patch.firstDeliveryCompleted = data.firstDeliveryCompleted;
          }
          if (Object.keys(patch).length) {
            freightDocPatches[id] = patch;
          }
        }
        stoppingPointsCacheRef.current.delete(id);
        queueCountCacheRef.current.delete(id);
        deleteTransportQueueCacheForFreight(transportQueueCountCacheRef.current, id);
        setQueueUiByFreightId((prev) => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setTransportQueueUiByFreightId((prev) => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      });
      if (!changedIds.size) return;
      freightListPerf("listeners: snapshot changes", {
        changed: changedIds.size,
        modified: modifiedIds.size,
        hasRemovals,
        serverFiltersActive,
      });
      if (hasRemovals) {
        changedIds.forEach((id) => {
          if (!modifiedIds.has(id)) {
            recentlyDeletedIdsRef.current.add(id);
          }
        });
        setData((prev) => {
          if (!Array.isArray(prev) || !prev.length) return prev;
          const removedOnly = [...changedIds].filter((id) => !modifiedIds.has(id));
          if (!removedOnly.length) return prev;
          const removedSet = new Set(removedOnly);
          return prev.filter((row) => !removedSet.has(row.id));
        });
      }
      if (isFilterApplyingRef.current) {
        return;
      }

      // Patch imediato com activeStatus do doc (quando denorm já gravou).
      if (Object.keys(freightDocPatches).length) {
        setData((prev) => {
          if (!Array.isArray(prev) || !prev.length) return prev;
          let changed = false;
          const next = prev.map((row) => {
            const patch = freightDocPatches[row.id];
            if (!patch) return row;
            changed = true;
            return { ...row, ...patch };
          });
          return changed ? next : prev;
        });
      }

      // Sempre atualiza substatus via SP só dos IDs modificados (também com filtro ativo).
      if (modifiedIds.size) {
        if (realtimeStatusTimerRef.current) {
          clearTimeout(realtimeStatusTimerRef.current);
        }
        const ids = [...modifiedIds];
        const patches = { ...freightDocPatches };
        realtimeStatusTimerRef.current = setTimeout(() => {
          if (isFilterApplyingRef.current) return;
          freightListPerf("listeners: debounced status refresh", {
            ids: ids.length,
            debounceMs: REALTIME_STATUS_REFRESH_DEBOUNCE_MS,
          });
          refreshOperationalStatusForIdsRef.current?.(ids, patches);
        }, REALTIME_STATUS_REFRESH_DEBOUNCE_MS);
      }

      // Re-fetch completo da página só sem filtro (outros campos); com filtro evita divergência.
      if (serverFiltersActive) {
        return;
      }
      if (realtimeRefreshTimerRef.current) {
        clearTimeout(realtimeRefreshTimerRef.current);
      }
      realtimeRefreshTimerRef.current = setTimeout(() => {
        if (isFilterApplyingRef.current) {
          return;
        }
        freightListPerf("listeners: debounced re-fetchData", {
          page: current,
          debounceMs: REALTIME_REFRESH_DEBOUNCE_MS,
        });
        fetchDataRef.current?.({
          page: current,
          reason: "realtimeSnapshot",
        });
      }, REALTIME_REFRESH_DEBOUNCE_MS);
    };

    const unsubscribers = chunks.map((chunk) =>
      db
        .collection("freight")
        .where(documentId, "in", chunk)
        .onSnapshot(handleSnapshot, () => {
          // noop: falha do listener não deve quebrar a tela
        }),
    );

    return () => {
      freightListPerf("listeners: unsubscribe", {
        chunks: chunks.length,
      });
      if (realtimeRefreshTimerRef.current) {
        clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
      if (realtimeStatusTimerRef.current) {
        clearTimeout(realtimeStatusTimerRef.current);
        realtimeStatusTimerRef.current = null;
      }
      unsubscribers.forEach((unsub) => {
        try {
          unsub?.();
        } catch {
          // noop
        }
      });
    };
  }, [db, user?.uid, current, visibleIdsKey, serverFiltersActive]);

  /**
   * Substatus mora em stopping_points. Escuta a subcoleção dos fretes visíveis.
   * Attach adiado (~2s) para não competir com o GET /freight/v2 na abertura.
   * O 1º snapshot hidrata o chip (reenter); os seguintes cobrem mudanças ao vivo.
   */
  useEffect(() => {
    if (!user?.uid) return undefined;
    if (!visibleIdsKey) return undefined;

    const visibleIds = visibleIdsKey.split("|").filter(Boolean);
    if (!visibleIds.length) return undefined;

    let cancelled = false;
    let unsubscribers = [];
    let flushTimer = null;
    const pendingIds = new Set();

    const scheduleStatusRefresh = (freightId) => {
      if (!freightId) return;
      pendingIds.add(freightId);
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(() => {
        const ids = [...pendingIds];
        pendingIds.clear();
        if (!ids.length || isFilterApplyingRef.current) return;
        freightListPerf("listeners: SP change → status refresh", {
          ids: ids.length,
        });
        ids.forEach((id) => stoppingPointsCacheRef.current.delete(id));
        refreshOperationalStatusForIdsRef.current?.(ids);
      }, REALTIME_STATUS_REFRESH_DEBOUNCE_MS);
    };

    const attachTimer = setTimeout(() => {
      if (cancelled) return;
      unsubscribers = visibleIds.map((freightId) =>
        db
          .collection("freight")
          .doc(freightId)
          .collection("stopping_points")
          .onSnapshot(
            () => {
              // 1º snapshot hidrata no reenter; seguintes = tempo real.
              scheduleStatusRefresh(freightId);
            },
            () => {
              // noop
            },
          ),
      );
      freightListPerf("listeners: SP subscribe", {
        visibleIds: visibleIds.length,
      });
    }, 2000);

    return () => {
      cancelled = true;
      clearTimeout(attachTimer);
      if (flushTimer) clearTimeout(flushTimer);
      if (unsubscribers.length) {
        freightListPerf("listeners: SP unsubscribe", {
          visibleIds: visibleIds.length,
        });
      }
      unsubscribers.forEach((unsub) => {
        try {
          unsub?.();
        } catch {
          // noop
        }
      });
    };
  }, [db, user?.uid, visibleIdsKey]);

  // ========================
  // localStorage filtros
  // ========================
  useEffect(() => {
    if (!user?.uid) return;

    const hydrateTiming = freightListPerfStart("localStorage-hydrate");
    try {
      const savedDateRange = localStorage.getItem(
        storageKey(user.uid, "freightDateRangeFilter"),
      );
      const savedColumnFilters = localStorage.getItem(
        storageKey(user.uid, "freightColumnFilters"),
      );
      const savedSorter = localStorage.getItem(
        storageKey(user.uid, "freightTableSorter"),
      );
      const savedColumnOrder = localStorage.getItem(
        storageKey(user.uid, "freightColumnOrder"),
      );

      freightListPerf("localStorage: keys", {
        hasDateRange: Boolean(savedDateRange),
        hasColumnFilters: Boolean(savedColumnFilters),
        hasSorter: Boolean(savedSorter),
        hasColumnOrder: Boolean(savedColumnOrder),
      });

      if (savedColumnOrder) {
        setColumnOrder(parseStoredColumnOrder(savedColumnOrder));
      } else {
        setColumnOrder(null);
      }

      // Carrega os filtros de coluna
      if (savedColumnFilters) {
        const parsedFilters = JSON.parse(savedColumnFilters);
        if (parsedFilters && typeof parsedFilters === "object") {
          setColumnFilters(normalizeColumnFiltersForV2(parsedFilters));
        } else {
          setColumnFilters({});
        }
      } else {
        setColumnFilters({});
      }

      
      const preserveFlagKey = storageKey(user.uid, PRESERVE_SORT_FLAG_KEY);
      let shouldRestoreSorter = false;
      try {
        shouldRestoreSorter = sessionStorage.getItem(preserveFlagKey) === "1";
        sessionStorage.removeItem(preserveFlagKey);
      } catch {
        shouldRestoreSorter = false;
      }

      if (savedSorter && shouldRestoreSorter) {
        const parsedSorter = JSON.parse(savedSorter);
        if (
          parsedSorter &&
          typeof parsedSorter === "object" &&
          typeof parsedSorter.columnKey === "string" &&
          (parsedSorter.order === "ascend" || parsedSorter.order === "descend")
        ) {
          setSorterConfig({
            columnKey: parsedSorter.columnKey,
            order: parsedSorter.order,
          });
        } else {
          setSorterConfig({ columnKey: null, order: null });
        }
      } else {
        setSorterConfig({ columnKey: null, order: null });
      }

      // Carrega o filtro de data e converte strings para dayjs
      if (savedDateRange) {
        const parsedDateRange = JSON.parse(savedDateRange);
        if (
          Array.isArray(parsedDateRange) &&
          parsedDateRange.length === 2 &&
          parsedDateRange[0] &&
          parsedDateRange[1]
        ) {
          // Converte strings ISO para objetos dayjs
          const startDate = dayjs(parsedDateRange[0]);
          const endDate = dayjs(parsedDateRange[1]);

          // Verifica se as datas são válidas
          if (startDate.isValid() && endDate.isValid()) {
            const nextRange = [startDate, endDate];
            setDateRange(nextRange);
            setDraftDateRange(nextRange);
            setSearchedColumn("dateColeta");
          } else {
            setDateRange([]);
            setDraftDateRange([]);
          }
        } else {
          setDateRange([]);
          setDraftDateRange([]);
        }
      } else {
        setDateRange([]);
        setDraftDateRange([]);
      }
    } catch (error) {
      console.error("Erro ao carregar filtros do localStorage:", error);
      setColumnFilters({});
      setDateRange([]);
      setDraftDateRange([]);
      setSorterConfig({ columnKey: null, order: null });
      setColumnOrder(null);
    }

    freightListPerfEnd(hydrateTiming, "localStorage: hydrate done");
    hasLoadedRef.current = true;
  }, [user?.uid]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!user?.uid) return;

    // Verifica se dateRange é um array com elementos válidos
    if (
      dateRange &&
      Array.isArray(dateRange) &&
      dateRange.length > 0 &&
      dateRange[0] &&
      dateRange[1]
    ) {
      localStorage.setItem(
        storageKey(user.uid, "freightDateRangeFilter"),
        JSON.stringify(dateRange),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightDateRangeFilter"));
    }

    // Verifica se columnFilters tem pelo menos uma chave
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      localStorage.setItem(
        storageKey(user.uid, "freightColumnFilters"),
        JSON.stringify(columnFilters),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightColumnFilters"));
    }

    if (sorterConfig?.columnKey && sorterConfig?.order) {
      localStorage.setItem(
        storageKey(user.uid, "freightTableSorter"),
        JSON.stringify(sorterConfig),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightTableSorter"));
    }

    if (Array.isArray(columnOrder) && columnOrder.length > 0) {
      localStorage.setItem(
        storageKey(user.uid, "freightColumnOrder"),
        JSON.stringify(columnOrder),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "freightColumnOrder"));
    }
  }, [user?.uid, dateRange, columnFilters, sorterConfig, columnOrder]);

  // ========================
  // Limpar filtros via prop externa
  // ========================
  useEffect(() => {
    if (cleanFilter) {
      recentlyDeletedIdsRef.current.clear();
      setColumnFilters({});
      setDateRange([]);
      setDraftDateRange([]);
      setSearchText("");
      setSearchedColumn("");
      setSorterConfig({ columnKey: null, order: null });
      setColumnOrder(null);
      if (user?.uid) {
        localStorage.removeItem(storageKey(user.uid, "freightDateRangeFilter"));
        localStorage.removeItem(storageKey(user.uid, "freightColumnFilters"));
        localStorage.removeItem(storageKey(user.uid, "freightTableSorter"));
        localStorage.removeItem(storageKey(user.uid, "freightColumnOrder"));
      }
      if (onCleanFilterHandled) {
        onCleanFilterHandled();
      }
    }
  }, [cleanFilter, user?.uid, onCleanFilterHandled]);

  // ========================
  // Abrir modal de filtros salvos via prop externa
  // ========================
  useEffect(() => {
    if (openFilter) {
      setOpenModalFilter(true);
      if (onOpenFilterHandled) {
        onOpenFilterHandled();
      }
    }
  }, [openFilter, onOpenFilterHandled]);

  // ========================
  // Modal fila (API-only)
  // ========================
  const handleOpen = () => setOpenModal(true);

  const handleClose = () => {
    setOpenModal(false);
    setDriversInFila([]);
  };

  const handleOpenTransport = () => setOpenTransportModal(true);

  const handleCloseTransport = () => {
    setOpenTransportModal(false);
    setTransportsInFila([]);
    setTransportFreightId(null);
    setTransportFreightShipperUid(null);
    setAcceptingTransportId(null);
  };

  const handleApplyCustomFilter = (filter) => {
    // Limpar filtros existentes antes de aplicar o novo
    const newFilters = {};
    let newDateRange = null;

    // Processar cada condição do filtro
    if (filter.conditions && filter.conditions.length > 0) {
      filter.conditions.forEach((condition) => {
        const { column, operator, value } = condition;

        // Tratar filtros de data separadamente
        if (column === "firstDelivery.dateColeta") {
          if (
            operator === "between" &&
            value &&
            Array.isArray(value) &&
            value.length === 2
          ) {
            const startDate =
              typeof value[0] === "string" ? dayjs(value[0]) : value[0];
            const endDate =
              typeof value[1] === "string" ? dayjs(value[1]) : value[1];
            newDateRange = [startDate, endDate];
          } else if (operator === "equals" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, date];
          } else if (operator === "after" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, null];
          } else if (operator === "before" && value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [null, date];
          } else if (value) {
            const date = typeof value === "string" ? dayjs(value) : value;
            newDateRange = [date, date];
          }
        }

        // Tratar filtros de status (valor único — GET /freight/v2)
        else if (column === "status.describe") {
          if (operator === "in" && Array.isArray(value) && value.length > 0) {
            newFilters[column] = value;
          } else if (operator === "equals" && value != null && value !== "") {
            const normalizedValue = Array.isArray(value) ? value[0] : value;
            newFilters[column] = [normalizedValue];
          }
        }
        // Tratar filtros numéricos com operadores condicionais
        else if (
          (column === "freight.valueFreightage" ||
            column === "driver.valueNegotiated") &&
          value
        ) {
          newFilters[column] = {
            operator,
            value: parseFloat(value) || 0,
          };
        }
        // Tratar outros filtros de texto/número, Para operador 'contains' ou 'equals' em campos de texto
        else {
          if (
            operator === "contains" ||
            operator === "equals" ||
            operator === "startsWith" ||
            operator === "endsWith"
          ) {
            newFilters[column] = value;
          }
        }
      });
    }

    // Aplicar os filtros de forma síncrona, primeiro aplicar o dateRange, depois os columnFilters
    if (newDateRange) {
      setDateRange(newDateRange);
      setDraftDateRange(newDateRange);
    }

    // Aplicar os filtros de coluna, bug encontrado de column + dateRange por isso essa ordem
    setColumnFilters(newFilters);
  };

  const getDriversQueue = async (id) => {
    try {
      const res = await freightApi2.queue(id);
      const queue = Array.isArray(res.data) ? res.data : res.data?.items || [];
      cacheSet(queueCountCacheRef.current, id, queue.length, TTL_QUEUE_COUNT_MS);
      setDriversInFila(queue);
      handleOpen();
    } catch (e) {
      message.error("Erro ao buscar fila do frete");
    }
  };

  const getTransportQueue = async (id, shipperUid) => {
    try {
      const isShipper = shipperUid || user?.uidShipper || "";
      if (!isShipper) {
        message.error("Embarcador não identificado para consultar a fila.");
        return;
      }

      const res = await freightApi2.transportQueue(id, isShipper);
      const queue = parseTransportQueueResponse(res);
      cacheSet(
        transportQueueCountCacheRef.current,
        `${id}:${isShipper}`,
        queue.length,
        TTL_QUEUE_COUNT_MS,
      );
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [id]: { count: queue.length, loading: false },
      }));
      setTransportsInFila(queue);
      setTransportFreightId(id);
      setTransportFreightShipperUid(isShipper);
      handleOpenTransport();
    } catch (e) {
      message.error("Erro ao buscar fila de transportadoras");
    }
  };

  const handleAcceptTransport = async (transport) => {
    if (!transportFreightId || !transport?.idTransport || acceptingTransportId) {
      return;
    }

    setAcceptingTransportId(transport.idTransport);

    try {
      await acceptTransportFromQueue({
        freightId: transportFreightId,
        transport,
        user,
      });

      message.success("Transportadora aceita com sucesso.");

      const isShipper =
        transportFreightShipperUid || user?.uidShipper || "";
      const res = await freightApi2.transportQueue(
        transportFreightId,
        isShipper,
      );
      const queue = parseTransportQueueResponse(res);
      setTransportsInFila(queue);
      cacheSet(
        transportQueueCountCacheRef.current,
        `${transportFreightId}:${isShipper}`,
        queue.length,
        TTL_QUEUE_COUNT_MS,
      );
      setTransportQueueUiByFreightId((prev) => ({
        ...prev,
        [transportFreightId]: { count: queue.length, loading: false },
      }));

      if (!queue.length) {
        handleCloseTransport();
        setTransportFreightId(null);
        setTransportFreightShipperUid(null);
      }
    } catch (error) {
      console.error("Erro ao aceitar transportadora:", error);
      message.error(
        getAcceptTransportErrorMessage(
          error,
          "Não foi possível aceitar a transportadora.",
        ),
      );
    } finally {
      setAcceptingTransportId(null);
    }
  };

  // ========================
  // Delete (API-only)
  // ========================

  const [open, setOpen] = React.useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const handleCloseYesOrNot = () => {
    setOpen(false);
    setPendingDeleteId(null);
  };
  const handleOpenYesOrNot = () => setOpen(true);

  const handleModalResult = async (value) => {
    if (!value || !pendingDeleteId) return;

    try {
      stoppingPointsCacheRef.current.delete(pendingDeleteId);
      queueCountCacheRef.current.delete(pendingDeleteId);
      deleteTransportQueueCacheForFreight(
        transportQueueCountCacheRef.current,
        pendingDeleteId,
      );
      setQueueUiByFreightId((prev) => {
        const next = { ...prev };
        delete next[pendingDeleteId];
        return next;
      });
      setTransportQueueUiByFreightId((prev) => {
        const next = { ...prev };
        delete next[pendingDeleteId];
        return next;
      });
      await freightApi2.delete(pendingDeleteId);
      message.success("Frete excluído com sucesso!");
      recentlyDeletedIdsRef.current.add(pendingDeleteId);
      fetchData({ skipStaleHydrate: true, page: current });
    } catch (error) {
      message.error("Erro ao excluir o frete");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const deleteEmb = (id) => {
    setPendingDeleteId(id);
    handleOpenYesOrNot();
  };

  // ========================
  // Filtros AntD (igual ao seu)
  // ========================
  const handleSearch = (selectedKeys, confirm, columnKey) => {
    setSearchText(selectedKeys[0]?.toString() ?? "");
    confirm();
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]:
        columnKey === "freight.valueFreightage" ||
          columnKey === "driver.valueNegotiated"
          ? moneyToInt(selectedKeys)
          : (selectedKeys.toString() ?? ""),
    }));
  };

  const handleSearchDate = (confirm) => {
    setDateRange(draftDateRange && draftDateRange.length ? draftDateRange : []);
    confirm();
    setSearchText("");
    setSearchedColumn("dateColeta");
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setColumnFilters((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[searchedColumn];
      return updatedFilters;
    });
  };

  const handleResetDate = (clearFilters) => {
    clearFilters();
    setDateRange([]);
    setDraftDateRange([]);
  };

  const getColumnSearchProps = (dataIndex, type = "text", maxLength = 40) => {
    const columnKey = Array.isArray(dataIndex)
      ? dataIndex.join(".")
      : dataIndex;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys = [],
        confirm,
        clearFilters,
        close,
      }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Buscar ${columnKey}`}
            value={selectedKeys[0] ?? ""}
            maxLength={type === "text" ? maxLength : undefined}
            onChange={(e) => {
              let value = e.target.value ?? "";
              if (type === "number") {
                value = value.replace(/\D/g, "");
              } else if (type === "money") {
                value = value.replace(/\D/g, "");
                value = (Number(value) / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              } else if (type === "text") {
                try {
                  value = value.replace(/[0-9]/g, "");
                } catch {
                  value = value.replace(/\d/g, "");
                }
              }
              setSelectedKeys(value ? [value] : []);
            }}
            onPressEnter={() => handleSearch(selectedKeys, confirm, columnKey)}
            style={{ marginBottom: 8, display: "block" }}
          />

          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, columnKey)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Buscar
            </Button>

            <Button
              onClick={() => {
                if (clearFilters) handleReset(clearFilters);

                columnFilters[columnKey] &&
                  setColumnFilters((prev) => {
                    const updatedFilters = { ...prev };
                    delete updatedFilters[columnKey];
                    return updatedFilters;
                  });

                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reiniciar
            </Button>

            <Button type="link" size="small" onClick={() => close()}>
              Fechar
            </Button>
          </Space>
        </div>
      ),

      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),

      // Filtros aplicados no servidor (GET /freight/v2)
      onFilter: () => true,

      onFilterDropdownOpenChange: (visible) => {
        if (visible) setTimeout(() => searchInput.current?.select(), 100);
      },

      render: (text) =>
        searchedColumn === columnKey ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={searchText ? [searchText] : []}
            autoEscape
            sanitize={foldDiacritics}
            textToHighlight={text ? text.toString() : ""}
          />
        ) : (
          text
        ),
    };
  };

  const getColumnSearchPropsDate = () => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePickerMenu
          value={draftDateRange}
          onChange={(dates) => {
            handleDateRangeChange(dates);
            setSelectedKeys(dates ? [dates] : []);
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearchDate(confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleResetDate(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reiniciar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: () => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === "dateColeta" ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  /** Filtro de status multi-checkbox com envio futuro de vários valores na API */
  const getStatusFilterProps = () => {
    const selectedValues = Array.isArray(columnFilters["status.describe"])
      ? columnFilters["status.describe"]
      : [];

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys = [],
        confirm,
        clearFilters,
      }) => {
        const currentSelected =
          Array.isArray(selectedKeys) && selectedKeys.length
            ? selectedKeys
            : selectedValues;
        const filteredOptions = filterStatusFilterOptions(statusSearchText);
        const allSelected =
          filteredOptions.length > 0 &&
          filteredOptions.every((option) => currentSelected.includes(option));
        const someSelected =
          filteredOptions.some((option) => currentSelected.includes(option)) &&
          !allSelected;

        const updateSelection = (nextValues) => {
          const normalized = Array.from(new Set(nextValues)).filter(Boolean);
          setSelectedKeys(normalized);
        };

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={statusFilterRef}
              placeholder="Buscar..."
              value={statusSearchText}
              onChange={(e) => setStatusSearchText(e.target.value)}
              style={{ marginBottom: 8 }}
              allowClear
              onPressEnter={() => confirm()}
            />

            <div style={{ maxHeight: 250, overflowY: "auto", marginBottom: 8 }}>
              <div style={{ marginBottom: 4 }}>
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) =>
                    updateSelection(
                      e.target.checked
                        ? Array.from(
                            new Set([...currentSelected, ...filteredOptions]),
                          )
                        : currentSelected.filter(
                            (value) => !filteredOptions.includes(value),
                          ),
                    )
                  }
                >
                  (Selecionar todos)
                </Checkbox>
              </div>

              {filteredOptions.map((value) => (
                <div key={value} style={{ marginBottom: 4 }}>
                  <Checkbox
                    checked={currentSelected.includes(value)}
                    onChange={(e) =>
                      updateSelection(
                        e.target.checked
                          ? [...currentSelected, value]
                          : currentSelected.filter((v) => v !== value),
                      )
                    }
                  >
                    {statusFilterOptionLabel(value)}
                  </Checkbox>
                </div>
              ))}
            </div>

            <Space>
              <Button
                onClick={() => {
                  if (clearFilters) clearFilters();
                  setSelectedKeys([]);
                  setStatusSearchText("");
                  setColumnFilters((prev) => {
                    const updated = { ...prev };
                    delete updated["status.describe"];
                    return updated;
                  });
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Resetar
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  const nextValues = Array.isArray(selectedKeys)
                    ? selectedKeys
                    : [];
                  setColumnFilters((prev) => {
                    const updated = { ...prev };
                    if (nextValues.length) updated["status.describe"] = nextValues;
                    else delete updated["status.describe"];
                    return updated;
                  });
                  confirm();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Aplicar
              </Button>
            </Space>
          </div>
        );
      },
      filterMultiple: true,
      onFilter: () => true,
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => statusFilterRef.current?.select(), 100);
          setStatusSearchText("");
        }
      },
    };
  };

  // Ordenação por prioridade (igual à sua ideia)
  const statusPriority = useMemo(
    () => ({
      Entregue: 1,
      "Em trânsito": 2,
      [FREIGHT_IN_TRANSIT_DESCRIBE]: 2,
      Contratado: 3,
      "Pendente de contratação": 4,
      "Pendente de validação": 5,
      Finalizado: 6,
    }),
    [],
  );

  const savedFilterStatusOptions = useMemo(
    () =>
      STATUS_FILTER_OPTION_VALUES.map((value) => ({
        value,
        label: statusFilterOptionLabel(value),
      })),
    [],
  );

  const baseColumns = [
    {
      title: "Data Coleta",
      dataIndex: ["firstDelivery", "dateColeta"],
      key: "dateColeta",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "dateColeta" ? sorterConfig.order : null,
      filteredValue: dateRange && dateRange.length ? [dateRange] : null,
      ...getColumnSearchPropsDate(),
      sorter: (a, b) => {
        const dateA = a.firstDelivery?.dateColeta;
        const dateB = b.firstDelivery?.dateColeta;

        if (!dateA) return -1;
        if (!dateB) return 1;

        const [yearA, monthA, dayA] = dateA.split("-").map(Number);
        const [yearB, monthB, dayB] = dateB.split("-").map(Number);

        return (
          new Date(yearA, monthA - 1, dayA).getTime() -
          new Date(yearB, monthB - 1, dayB).getTime()
        );
      },
      render: (dataColeta) => {
        if (dataColeta && dataColeta !== "") {
          const [year, month, day] = dataColeta.split("-");
          const localDate = new Date(+year, +month - 1, +day);
          return format(localDate, "dd/MM/yyyy");
        }
        return "";
      },
    },
    {
      title: "Código Frete",
      dataIndex: "numberSerial",
      key: "numberSerial",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "numberSerial" ? sorterConfig.order : null,
      filteredValue: columnFilters["numberSerial"]
        ? [columnFilters["numberSerial"]]
        : null,
      ...getColumnSearchProps("numberSerial", "number"),
      sorter: (a, b) =>
        (a.numberSerial?.toString() || "").localeCompare(
          b.numberSerial?.toString() || "",
        ),
      render: (value) => <span>{value}</span>,
    },
    {
      title: "Nome Pagador",
      dataIndex: ["clientPayment", "name"],
      key: "nameClient",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "nameClient" ? sorterConfig.order : null,
      filteredValue: columnFilters["clientPayment.name"]
        ? [columnFilters["clientPayment.name"]]
        : null,
      ...getColumnSearchProps(["clientPayment", "name"], "text", 40),
      sorter: (a, b) =>
        (a.clientPayment?.name || "").localeCompare(
          b.clientPayment?.name || "",
        ),
    },
    {
      title: 'Nome Motorista',
      dataIndex: ['freight', 'getDriverFreight', 'name'],
      key: 'nameDriver',
      align: 'center',
      filteredValue: columnFilters['freight.getDriverFreight.name'] ? [columnFilters['freight.getDriverFreight.name']] : null,
      ...getColumnSearchProps(['freight', 'getDriverFreight', 'name'], 'text', 40),
      sorter: (a, b) => {
        const nameA = a.driver?.name || '';
        const nameB = b.driver?.name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Origem",
      dataIndex: ["firstDelivery", "city"],
      key: "firstDeliveryCity",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "firstDeliveryCity"
          ? sorterConfig.order
          : null,
      filteredValue: columnFilters["firstDelivery.city"]
        ? [columnFilters["firstDelivery.city"]]
        : null,
      ...getColumnSearchProps(["firstDelivery", "city"], "text", 40),
      render: (_, record) => (
        <span>
          {record?.firstDelivery?.city || ""}{" "}
          {record?.firstDelivery?.uf
            ? `- ${record.firstDelivery.uf}`
            : ""}
        </span>
      ),
      sorter: (a, b) =>
        (a.firstDelivery?.city || "").localeCompare(
          b.firstDelivery?.city || "",
        ),
    },
    {
      title: "Destino",
      dataIndex: ["lastDelivery", "city"],
      key: "lastDeliveryCity",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "lastDeliveryCity"
          ? sorterConfig.order
          : null,
      filteredValue: columnFilters["lastDelivery.city"]
        ? [columnFilters["lastDelivery.city"]]
        : null,
      ...getColumnSearchProps(["lastDelivery", "city"], "text", 40),
      render: (_, record) => (
        <span>{record?.lastDelivery?.city || ""} - {record?.lastDelivery?.uf || ""}</span>
      ),
      sorter: (a, b) =>
        (a.lastDelivery?.city || "").localeCompare(b.lastDelivery?.city || ""),

    },
    ...(canViewFreightFreight &&
    !(isTransportadoraUser && user?.viewALL !== true)
      ? [
      {
        title: 'Valor do Frete',
        dataIndex: ['freight', 'valueFreightage'],
        key: 'freight',
        align: 'center',
        // width: '22%',
        sortOrder://LIDA PELO AND PARA DESENHAR A SETA 
          sorterConfig.columnKey === "freight" ? sorterConfig.order : null, //RECEBE DO ONCHANGE 'QUAL' E A 'ORDEM'
        filteredValue: columnFilters['freight.valueFreightage'] ? [columnFilters['freight.valueFreightage']] : null,
        ...getColumnSearchProps(['freight', 'valueFreightage'], 'money'),
        render: (valueFreightage) => (
          `${(valueFreightage || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`
        ),
        sorter: (first, second) => {
          const valueFirst = first.freight?.valueFreightage || 0;
          const valueSecond = second.freight?.valueFreightage || 0;
          return valueFirst - valueSecond;
        },
      },
    ] : []),
    ...(canViewFreightDriver ? [
      {
        title: 'Pagando ao Motorista',
        dataIndex: ['driver', 'valueNegotiated'],
        key: 'valueNegotiated',
        align: 'center',
        // width: '30%',
        sortOrder:
          sorterConfig.columnKey === "valueNegotiated"
            ? sorterConfig.order
            : null,
        filteredValue: columnFilters['driver.valueNegotiated'] ? [columnFilters['driver.valueNegotiated']] : null,
        ...getColumnSearchProps(['driver', 'valueNegotiated'], 'money'),
        render: (driver, record) => {
          const hideValue = shouldHideDriverNegotiationOnFreightRecord(record, {
            viewALL: user?.viewALL === true,
            isViewerTransport: isTransportadoraUser,
          });
          if (hideValue) return '—';
          return `${(driver || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}`;
        },
        sorter: (first, second) => {
          const valueFirst = first.driver?.valueNegotiated || 0;
          const valueSecond = second.driver?.valueNegotiated || 0;
          return valueFirst - valueSecond;
        },
      },
    ] : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      sortOrder:
        sorterConfig.columnKey === "status" ? sorterConfig.order : null,
      filteredValue:
        Array.isArray(columnFilters["status.describe"]) &&
        columnFilters["status.describe"].length
          ? columnFilters["status.describe"]
          : null,
      ...getStatusFilterProps(),
      render: (status, record) => {
        const effectiveStatusDescribe = getStatusDescribeForRecord(record);
        const describeText =
          effectiveStatusDescribe === "Pendente de validação"
            ? "P. Validação"
            : effectiveStatusDescribe === "Pendente de contratação"
              ? "P.Contratação"
              : effectiveStatusDescribe;
        return (
          <Tooltip title={effectiveStatusDescribe}>
            <div>{renderStatusColor(describeText)}</div>
          </Tooltip>
        );
      },
      sorter: (a, b) =>
        getStatusDescribeForRecord(a).localeCompare(getStatusDescribeForRecord(b)),
    },
    {
      title: "Ações",
      align: "center",
      key: "action",
      render: (_, record) => {
        const handleMenuClick = ({ key }) => {
          if (key === "queue") getDriversQueue(record.id);
          if (key === "delete") deleteEmb(record.id);
        };

        const menu = (
          <Menu
            onClick={handleMenuClick}
            style={{
              minWidth: 200,
              padding: "8px 0",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            <Menu.Item key="queue" style={menuItemStyle}>
              <Link
                to={`#${record.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
              >
                <DriverBadge
                  recordId={record.id}
                  statusDescribe={getStatusDescribeForRecord(record)}
                  queueCount={queueUiByFreightId[record.id]?.count ?? 0}
                  queueLoading={!!queueUiByFreightId[record.id]?.loading}
                />
                Ver Fila
              </Link>
            </Menu.Item>

            <Menu.Item key="edit" style={menuItemStyle}>
              {user?.permissoes?.freight?.isEdit ||
                user?.permissoes?.frete?.editar ? (
                <Link
                  to={`/freightCopy/${record.id}`}
                  onClick={() => markPreserveSortOnReturn(user?.uid)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <EditOutlined style={{ fontSize: 18 }} />
                  Editar frete
                </Link>
              ) : (
                <Link
                  to={`/freightCopy/${record.id}`}
                  state={{ viewOnly: true }}
                  onClick={() => markPreserveSortOnReturn(user?.uid)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                  }}
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                  Visualizar frete
                </Link>
              )}
            </Menu.Item>

            <Menu.Item key="copy" style={menuItemStyle}>
              <Link
                to={`/freightCopy/${record.id}?copy=true`}
                onClick={() => markPreserveSortOnReturn(user?.uid)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                }}
              >
                <CopyOutlined style={{ fontSize: 18 }} />
                Copiar
              </Link>
            </Menu.Item>

            <div
              style={{
                height: 1,
                background: "rgba(1,36,66,0.1)",
                margin: "6px 8px",
              }}
            />

            <Menu.Item
              key="delete"
              style={{
                ...menuItemStyle,
                color: "#ff0000",
              }}
            >
              {user?.permissoes?.freight?.isDelete ||
                user?.permissoes?.frete?.excluir ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <DeleteOutlined style={{ fontSize: 18 }} />
                  Excluir frete
                </div>
              ) : null}
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown
            overlay={menu}
            trigger={["click"]}
            placement="bottomRight"
            onOpenChange={(open) => {
              if (open) {
                prefetchQueueCountForFreight(record.id);
                prefetchTransportQueueCountForFreight(
                  record.id,
                  resolveEmbarcadorUid(record, user),
                );
              }
            }}
          >
            <Button
              type="text"
              icon={<SmallDashOutlined style={{ fontSize: 20 }} />}
            />
          </Dropdown>
        );
      },
    },
  ];

  const { orderedColumns, tableComponents } = useDraggableColumnOrder({
    baseColumns,
    columnOrder,
    onColumnOrderChange: setColumnOrder,
  });


  const sortedData = useMemo(() => {
    if (!sorterConfig?.columnKey || !sorterConfig?.order) return datas;

    const column = baseColumns.find((c) => c.key === sorterConfig.columnKey);
    if (typeof column?.sorter !== "function") return datas;

    const sorted = [...allDatas].sort(column.sorter);
    return sorterConfig.order === "descend" ? sorted.reverse() : sorted;
  }, [datas, allDatas, sorterConfig, baseColumns]);

  const hasActiveSort = Boolean(sorterConfig?.columnKey && sorterConfig?.order);

  const paginationEstimatedTotal =
    (current - 1) * pageSize + datas.length + (listHasMore ? pageSize : 0);

  return (
    <>
      <DialogFileDriver
        driversInFila={driversInFila}
        openModal={openModal}
        handleClose={handleClose}
      />

      <DialogFilaTransport
        transportsInFila={transportsInFila}
        openModal={openTransportModal}
        handleClose={handleCloseTransport}
        acceptingTransportId={acceptingTransportId}
        onAcceptTransport={handleAcceptTransport}
        canAccept={Boolean(user?.uidShipper)}
      />

      <ModalFilter
        visible={openModalFilter}
        onClose={() => setOpenModalFilter(false)}
        onApply={handleApplyCustomFilter}
        selectOptionsMap={{ "status.describe": savedFilterStatusOptions }}
      />

      <YesOrNot
        open={open}
        handleClose={handleCloseYesOrNot}
        onResult={handleModalResult}
      />

    
      <Table
        columns={orderedColumns}
        components={tableComponents}
        dataSource={sortedData}
        rowKey="id"
        loading={hasActiveSort ? loadingAll : (loading || isFilterApplying)}
        onChange={(_pagination, _filters, sorter) => {
          if (sorter && sorter.columnKey && sorter.order) {
            setSorterConfig({
              columnKey: sorter.columnKey,
              order: sorter.order,
            });    //SALVA AQUI NO SETSORTECONFIG E ATUALIZA SORTERCONFIG
          } else {
            setSorterConfig({ columnKey: null, order: null });
          }
        }}
        pagination={
          hasActiveSort
            ? {
                
                current,
                pageSize,
                total: sortedData.length,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
                showQuickJumper: false,
                hideOnSinglePage: false,
                onChange: (page, newPageSize) => {
                  setCurrent(page);
                  if (newPageSize && newPageSize !== pageSize) {
                    setPageSize(newPageSize);
                  }
                },
              }
            : {
                
                current,
                pageSize,
                total: paginationEstimatedTotal,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
                showQuickJumper: false,
                hideOnSinglePage: false,
                onChange: (page, newPageSize) => {
                  if (newPageSize && newPageSize !== pageSize) {
                    setPageSize(newPageSize);
                    setCurrent(1);
                    return;
                  }
                  if (page > current && !listHasMore) {
                    message.warning("Nao ha mais dados para proxima pagina.");
                    return;
                  }
                  setCurrent(page);
                },
              }
        }
      />
    </>
  );
};

export default DataFreteTableCopy;
